import { AuthCallback } from './components/AuthCallback';
import { PageErrorBoundary, SectionErrorBoundary } from './components/ErrorBoundary';
import { ErrorFallback } from './components/ErrorFallback';
import FlowController from './components/FlowController';
import LandingSplash from './components/LandingSplash';
import { LoginScreen } from './components/LoginScreen';
import ProjectManager from './components/ProjectManager';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider } from './lib/errorContext';
import { useAppStore } from './store/hooks';

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, user, hasLocalSession, authStatus, isReady } = useAuth();
  const currentProjectId = useAppStore((s) => s.currentProjectId);
  const projects = useAppStore((s) => s.projects);

  // Debug logging
  console.log('[AuthenticatedApp] Render state:', {
    isAuthenticated,
    isLoading,
    user: user?.email,
    projectCount: Object.keys(projects).length,
    currentProjectId,
    hasLocalSession,
    authStatus,
    isReady,
  });

  // All data is stored locally; no cloud sync.

  // Check if this is the dedicated auth callback page
  const isAuthCallbackPage = window.location.pathname === '/auth/callback';

  // Check if this is an auth callback (magic link clicked)
  // Renamed for clarity, as this is a route-based check
  const isAuthCallbackRoute = () => {
    const hash = window.location.hash;
    const search = window.location.search;
    return (
      hash.includes('access_token=') ||
      hash.includes('error=') ||
      search.includes('access_token=') ||
      search.includes('error=') ||
      search.includes('code=') // For PKCE flow if provider redirects with code
    );
  };

  // Always show auth callback component on the dedicated page or when auth params are present
  if (isAuthCallbackPage || isAuthCallbackRoute()) {
    console.log('[AuthenticatedApp] Auth callback detected:', {
      isAuthCallbackPage,
      isAuthCallbackRoute: isAuthCallbackRoute(),
    });
    return (
      <SectionErrorBoundary fallbackComponent={ErrorFallback}>
        <AuthCallback />
      </SectionErrorBoundary>
    );
  }

  // Show loading only when truly necessary:
  // 1. AuthContext is not ready yet (!isReady)
  // 2. We're loading without any local data to show
  const showGlobalLoading = !isReady || (isLoading && !hasLocalSession);

  if (showGlobalLoading) {
    console.log('[AuthenticatedApp] Showing global loading screen:', {
      isReady,
      isLoading,
      hasLocalSession,
      authStatus,
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4f46e5] mx-auto"></div>
          <p className="mt-4 text-gray-400">
            {authStatus === 'authenticating' ? 'Finalizing login...' : 'Loading application...'}
          </p>
        </div>
      </div>
    );
  }

  // If we have local session but are still authenticating, show app with local data
  // and let authentication happen in the background

  // If authenticated or has a local session to show, render the app content
  if (isAuthenticated || hasLocalSession) {
    console.log('[AuthenticatedApp] Showing app content:', {
      isAuthenticated,
      hasLocalSession,
      projectsLength: Object.keys(projects).length,
      currentProjectId,
    });
    if (Object.keys(projects).length === 0) {
      return (
        <SectionErrorBoundary fallbackComponent={ErrorFallback}>
          <LandingSplash />
        </SectionErrorBoundary>
      );
    }
    if (!currentProjectId) {
      return (
        <SectionErrorBoundary fallbackComponent={ErrorFallback}>
          <ProjectManager />
        </SectionErrorBoundary>
      );
    }
    return (
      <SectionErrorBoundary fallbackComponent={ErrorFallback}>
        <FlowController />
      </SectionErrorBoundary>
    );
  }

  // Fallback: If not loading, not authenticated, and no local session, show LoginScreen
  console.log('[AuthenticatedApp] All checks failed, showing LoginScreen as fallback.');
  return (
    <SectionErrorBoundary fallbackComponent={ErrorFallback}>
      <LoginScreen />
    </SectionErrorBoundary>
  );
}

export function App() {
  return (
    <AuthProvider>
      <ErrorProvider>
        <PageErrorBoundary fallbackComponent={ErrorFallback}>
          <AuthenticatedApp />
        </PageErrorBoundary>
      </ErrorProvider>
    </AuthProvider>
  );
}
